import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChartBar, Pause, Plus, Trash, Warning, CheckCircle, Clock, ArrowUp, ArrowDown } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// ========== PLANNED VS ACTUAL ==========
export const PlannedVsActual = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await axios.get(`${API}/api/projects/${projectId}/planned-vs-actual`, { withCredentials: true }); setData(res.data); } catch {}
      setLoading(false);
    };
    fetch();
  }, [projectId]);

  if (loading) return <div className="py-6 text-center text-zinc-400 text-sm">Loading...</div>;
  if (!data || (data.work_progress?.length === 0 && data.material_comparison?.length === 0)) return null;

  const s = data.summary || {};
  const STATUS_STYLE = {
    not_started: { bg: 'bg-zinc-100', text: 'text-zinc-500', label: 'Not Started' },
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' },
    on_track: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'On Track' },
    over_executed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Over' },
    under: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Under' },
    over: { bg: 'bg-red-100', text: 'text-red-700', label: 'Over' },
  };

  return (
    <div className="space-y-6" data-testid="planned-vs-actual">
      <div className="flex items-center gap-2">
        <ChartBar size={18} className="text-blue-700" />
        <h3 className="text-base font-bold text-zinc-900">Planned vs Actual</h3>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">Total Items</p><p className="text-xl font-bold text-zinc-900 mt-1">{s.total_work_items}</p></div>
        <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">Not Started</p><p className="text-xl font-bold text-zinc-500 mt-1">{s.not_started}</p></div>
        <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">In Progress</p><p className="text-xl font-bold text-blue-700 mt-1">{s.in_progress}</p></div>
        <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">On Track</p><p className="text-xl font-bold text-emerald-700 mt-1">{s.on_track}</p></div>
      </div>

      {/* Work Progress */}
      {data.work_progress?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-zinc-600 uppercase tracking-wider mb-2">BOQ vs DPR Progress</h4>
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase text-zinc-500">
                <th className="p-2.5 text-left">Item</th>
                <th className="p-2.5 text-right w-24">Planned</th>
                <th className="p-2.5 text-right w-24">Actual</th>
                <th className="p-2.5 text-right w-24">Variance</th>
                <th className="p-2.5 text-center w-24">Status</th>
              </tr></thead>
              <tbody>{data.work_progress.map((w, i) => {
                const st = STATUS_STYLE[w.status] || STATUS_STYLE.not_started;
                return (
                  <tr key={i} className="border-b border-zinc-100" data-testid={`work-row-${i}`}>
                    <td className="p-2.5"><span className="font-medium text-zinc-800">{w.description}</span><span className="text-zinc-400 ml-1.5 text-[10px]">{w.unit}</span></td>
                    <td className="p-2.5 text-right text-zinc-700">{w.planned_qty}</td>
                    <td className="p-2.5 text-right text-zinc-700">{w.actual_qty}</td>
                    <td className="p-2.5 text-right">
                      <span className={w.variance > 0 ? 'text-red-600' : w.variance < 0 ? 'text-amber-600' : 'text-zinc-400'}>
                        {w.variance > 0 ? '+' : ''}{w.variance} {w.variance !== 0 && <span className="text-[9px]">({w.variance_pct > 0 ? '+' : ''}{w.variance_pct}%)</span>}
                      </span>
                    </td>
                    <td className="p-2.5 text-center"><span className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Material Comparison */}
      {data.material_comparison?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-zinc-600 uppercase tracking-wider mb-2">Material Demand vs Received</h4>
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase text-zinc-500">
                <th className="p-2.5 text-left">Material</th>
                <th className="p-2.5 text-left w-16">Unit</th>
                <th className="p-2.5 text-right w-24">Planned</th>
                <th className="p-2.5 text-right w-24">Received</th>
                <th className="p-2.5 text-right w-24">Variance</th>
                <th className="p-2.5 text-center w-20">Status</th>
              </tr></thead>
              <tbody>{data.material_comparison.map((m, i) => {
                const st = STATUS_STYLE[m.status] || STATUS_STYLE.not_started;
                return (
                  <tr key={i} className="border-b border-zinc-100" data-testid={`mat-compare-row-${i}`}>
                    <td className="p-2.5 font-medium text-zinc-800">{m.material_name}</td>
                    <td className="p-2.5 text-zinc-500">{m.unit}</td>
                    <td className="p-2.5 text-right text-zinc-700">{m.planned_qty}</td>
                    <td className="p-2.5 text-right text-zinc-700">{m.received_qty}</td>
                    <td className="p-2.5 text-right"><span className={m.variance > 0 ? 'text-red-600' : m.variance < 0 ? 'text-amber-600' : 'text-zinc-400'}>{m.variance > 0 ? '+' : ''}{m.variance}</span></td>
                    <td className="p-2.5 text-center"><span className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== HALT ENTRIES ==========
export const HaltPanel = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), duration_hours: '', skilled_count: '', unskilled_count: '', reason: '', trade: '', remarks: '', billable: false, boq_item_id: '' });

  const fetchHalts = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/projects/${projectId}/halts`, { withCredentials: true }); setData(res.data); } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchHalts(); }, [fetchHalts]);

  const handleSave = async () => {
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    if (!form.duration_hours || parseFloat(form.duration_hours) <= 0) { toast.error('Duration must be > 0'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/projects/${projectId}/halts`, {
        ...form,
        duration_hours: parseFloat(form.duration_hours),
        skilled_count: parseInt(form.skilled_count) || 0,
        unskilled_count: parseInt(form.unskilled_count) || 0,
      }, { withCredentials: true });
      toast.success('Halt recorded');
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0, 10), duration_hours: '', skilled_count: '', unskilled_count: '', reason: '', trade: '', remarks: '', billable: false, boq_item_id: '' });
      fetchHalts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this halt entry?')) return;
    try { await axios.delete(`${API}/api/halts/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchHalts(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="py-6 text-center text-zinc-400 text-sm">Loading...</div>;

  const s = data?.summary || {};
  const entries = data?.entries || [];

  return (
    <div className="space-y-4" data-testid="halt-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pause size={18} className="text-red-600" />
          <h3 className="text-base font-bold text-zinc-900">Project Halts</h3>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-none text-xs" data-testid="add-halt-btn">
          <Plus size={14} className="mr-1" /> Record Halt
        </Button>
      </div>

      {/* Summary */}
      {entries.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">Total Lost</p><p className="text-xl font-bold text-red-700 mt-1">{s.total_lost_man_days}<span className="text-xs text-zinc-400 ml-1">days</span></p></div>
          <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">Skilled</p><p className="text-xl font-bold text-blue-700 mt-1">{s.skilled_lost_man_days}<span className="text-xs text-zinc-400 ml-1">days</span></p></div>
          <div className="bg-white border border-zinc-200 p-3"><p className="text-[9px] text-zinc-500 font-bold uppercase">Unskilled</p><p className="text-xl font-bold text-zinc-700 mt-1">{s.unskilled_lost_man_days}<span className="text-xs text-zinc-400 ml-1">days</span></p></div>
          <div className="bg-white border border-zinc-200 p-3">
            <p className="text-[9px] text-zinc-500 font-bold uppercase">Billable Split</p>
            <div className="flex gap-2 mt-1">
              <span className="text-xs font-bold text-amber-700">{s.billable_man_days}d billable</span>
              <span className="text-xs text-zinc-400">|</span>
              <span className="text-xs font-bold text-zinc-500">{s.non_billable_man_days}d non-billable</span>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3" data-testid="halt-form">
          <div className="grid grid-cols-4 gap-3">
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Date</label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-none text-xs" data-testid="halt-date" /></div>
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Duration (hrs) *</label><Input type="number" value={form.duration_hours} onChange={e => setForm({...form, duration_hours: e.target.value})} placeholder="e.g. 4" className="rounded-none text-xs" data-testid="halt-hours" /></div>
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Skilled Count</label><Input type="number" value={form.skilled_count} onChange={e => setForm({...form, skilled_count: e.target.value})} placeholder="0" className="rounded-none text-xs" data-testid="halt-skilled" /></div>
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Unskilled Count</label><Input type="number" value={form.unskilled_count} onChange={e => setForm({...form, unskilled_count: e.target.value})} placeholder="0" className="rounded-none text-xs" data-testid="halt-unskilled" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Reason *</label><Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="e.g. Rain delay, Power failure" className="rounded-none text-xs" data-testid="halt-reason" /></div>
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Trade</label><Input value={form.trade} onChange={e => setForm({...form, trade: e.target.value})} placeholder="e.g. Civil Works" className="rounded-none text-xs" /></div>
          </div>
          <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Remarks</label><Input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} placeholder="Additional notes..." className="rounded-none text-xs" /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={form.billable} onChange={e => setForm({...form, billable: e.target.checked})} className="rounded border-zinc-300 h-3.5 w-3.5" data-testid="halt-billable" /><span className="text-zinc-700 font-medium">Billable to client</span></label>
            <div className="flex-1" />
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm" className="rounded-none border-zinc-300 text-xs">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-none text-xs" data-testid="save-halt-btn">{saving ? 'Saving...' : 'Record Halt'}</Button>
          </div>
        </div>
      )}

      {/* Entries List */}
      {entries.length > 0 && (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase text-zinc-500">
              <th className="p-2.5 text-left">Date</th>
              <th className="p-2.5 text-left">Reason</th>
              <th className="p-2.5 text-right w-16">Hours</th>
              <th className="p-2.5 text-right w-16">Skilled</th>
              <th className="p-2.5 text-right w-16">Unskilled</th>
              <th className="p-2.5 text-right w-20">Lost Days</th>
              <th className="p-2.5 text-center w-16">Billable</th>
              <th className="p-2.5 w-8"></th>
            </tr></thead>
            <tbody>{entries.map((e, i) => (
              <tr key={e.id} className="border-b border-zinc-100" data-testid={`halt-row-${i}`}>
                <td className="p-2.5 text-zinc-700 font-mono text-[10px]">{e.date}</td>
                <td className="p-2.5"><span className="text-zinc-800 font-medium">{e.reason}</span>{e.trade && <span className="text-zinc-400 ml-1 text-[10px]">{e.trade}</span>}{e.remarks && <span className="block text-[10px] text-zinc-400 mt-0.5">{e.remarks}</span>}</td>
                <td className="p-2.5 text-right text-zinc-700">{e.duration_hours}</td>
                <td className="p-2.5 text-right text-zinc-700">{e.skilled_count}</td>
                <td className="p-2.5 text-right text-zinc-700">{e.unskilled_count}</td>
                <td className="p-2.5 text-right font-bold text-red-700">{e.total_lost_man_days}</td>
                <td className="p-2.5 text-center">{e.billable ? <span className="text-[8px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Yes</span> : <span className="text-zinc-300">-</span>}</td>
                <td className="p-2.5"><button onClick={() => handleDelete(e.id)} className="text-red-400 hover:text-red-600"><Trash size={13} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {entries.length === 0 && !showForm && <p className="text-xs text-zinc-400 text-center py-6">No halts recorded. Click "Record Halt" to log disruptions.</p>}
    </div>
  );
};
